import React, { useContext, useEffect, useRef, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import BarLoader from 'react-spinners/BarLoader';
import Footer from './components/Footer/Footer';
import {
    CompanyCultures,
    ICompany,
    IContactInfo,
    FrontendType,
    defaultContactInfo,
    hasContactInfo,
    ICustomCssAndJs,
} from './entities/company';
import { IUser } from './entities/user';
import {
    getActivePockets,
    getCompanyData,
    getContactInfo,
    getCustomStyleSheetAndScript,
} from './services/api/company';
import { getUserWithInvoice } from './services/api/user';
import makeCancelable, { ICancelablePromise } from './services/cancelablePromise';
import smartlookClient from 'smartlook-client';
import config from './constants';
import { Helmet } from 'react-helmet';
import { IEmbedOptions, EmbedModeType, readEmbedOptions, defaultEmbedOptions } from './embed';
import AvailabilityWarning from './components/AvailabilityWarning';
import Main from './Main';
import Header from './components/Header/Header';
import { CustomScript } from './components/CustomScript/CustomScript';
import CookieConsentFooter from './components/CookieConsentFooter/CookieConsentFooter';
import { AppProvider, IAppProvidedState } from './contexts/AppContext';
import { ContactProvider } from './contexts/ContactContext';
import { EmbedProvider } from './contexts/EmbedContext';
import { IntlContext } from './contexts/IntlContext';
import { IPocket, PaymentProvider } from './entities/pocket';
import JsApiContextProvider from './contexts/JsApi/JsApiContextProvider';
import { noop } from './services/noop';
import HeadMetaData from './components/HeadMetaData/HeadMetaData';

const App: React.FC = () => {
    const context = useContext(IntlContext);
    const companySubscription = useRef<ICancelablePromise<ICompany>>();
    const userSubscription = useRef<ICancelablePromise<IUser | null>>();
    const contactInfoSubscription = useRef<ICancelablePromise<IContactInfo>>();
    const activePocketsSubscription = useRef<ICancelablePromise<IPocket[]>>();
    const customCssAndJsSubscription = useRef<ICancelablePromise<ICustomCssAndJs>>();
    const [isLoading, setIsLoading] = useState(false);
    const [contactInfo, setContactInfo] = useState<IContactInfo | null>(null);
    const [providedState, setProvidedState] = useState<IAppProvidedState>({
        company: null,
        user: null,
        isAuthenticated: undefined,
        actions: {
            setUser: noop,
            setLoading: noop,
        },
    });
    const [customCss, setCustomCss] = useState('');
    const [customJs, setCustomJs] = useState('');
    const [embedOptions, setEmbedOptions] = useState<IEmbedOptions>(defaultEmbedOptions);
    const [activeBarionPockets, setActiveBarionPockets] = useState<IPocket[]>([]);
    const [activeSimplePayPockets, setActiveSimplePayPockets] = useState<IPocket[]>([]);

    const isEmbedded = embedOptions.mode !== EmbedModeType.WebPage;
    const { company } = providedState;

    const setUser = (userData: IUser | null) =>
        setProvidedState({
            ...providedState,
            user: userData,
            isAuthenticated: !!userData,
        });

    const setLoading = (value: boolean) => setIsLoading(value);

    useEffect(() => {
        if (config.SMARTLOOK_CODE) {
            try {
                smartlookClient.init(config.SMARTLOOK_CODE);
            } catch (error) {
                // Ignore smartlook init error
            }
        }

        setEmbedOptions(readEmbedOptions);

        companySubscription.current = makeCancelable<ICompany>(getCompanyData());
        customCssAndJsSubscription.current = makeCancelable<ICustomCssAndJs>(
            getCustomStyleSheetAndScript()
        );
        userSubscription.current = makeCancelable<IUser | null>(getUserWithInvoice());

        Promise.all([
            companySubscription.current.promise,
            customCssAndJsSubscription.current.promise,
            userSubscription.current.promise,
        ])
            .then(([fetchedCompany, cssJsData, userData]) => {
                setProvidedState({
                    ...providedState,
                    company: fetchedCompany,
                    user: userData,
                    isAuthenticated: !!userData,
                });

                if (!context.alreadySwitched) {
                    const urlSearchParams = new URLSearchParams(window.location.search);
                    const cultureParam =
                        urlSearchParams.get('culture') || urlSearchParams.get('Culture');
                    const formalParam =
                        urlSearchParams.get('formal') || urlSearchParams.get('Formal');

                    const isFormal =
                        formalParam === null
                            ? fetchedCompany.IsFormalCulture
                            : formalParam === 'true';
                    const culture =
                        cultureParam === null
                            ? CompanyCultures[fetchedCompany.DefaultUserCulture]
                            : cultureParam;

                    if (culture !== context.locale || isFormal !== context.isFormal) {
                        context.setLocale(culture, isFormal);
                    }
                }

                if (cssJsData) {
                    setCustomCss(cssJsData.StyleSheet);
                    setCustomJs(cssJsData.Script);
                }
            })
            .finally(() => setLoading(false))
            .catch(() => null);

        contactInfoSubscription.current = makeCancelable<IContactInfo>(getContactInfo());
        contactInfoSubscription.current.promise.then(
            (result) => {
                setContactInfo(result);
            },
            () => null
        );

        activePocketsSubscription.current = makeCancelable<IPocket[]>(getActivePockets());
        activePocketsSubscription.current.promise.then(
            (activePockets) => {
                const newActiveBarionPockets = activePockets.filter(
                    (pocket) => pocket.PaymentProvider === PaymentProvider.Barion
                );
                const newActiveSimplePayPockets = activePockets.filter(
                    (pocket) => pocket.PaymentProvider === PaymentProvider.SimplePay
                );
                setActiveBarionPockets(newActiveBarionPockets);
                setActiveSimplePayPockets(newActiveSimplePayPockets);
            },
            () => null
        );

        return () => {
            customCssAndJsSubscription?.current?.cancel();
            companySubscription?.current?.cancel();
            userSubscription?.current?.cancel();
            contactInfoSubscription?.current?.cancel();
            activePocketsSubscription?.current?.cancel();
        };
    }, []);

    if (company && company.FrontendType === FrontendType.Inactive) {
        return <AvailabilityWarning type="inactiveFE" modifier="app-level" />;
    }

    return (
        <>
            {company && <HeadMetaData companyName={company.Name} />}
            <Helmet>
                {!!customCss && <link rel="stylesheet" href={customCss} />}
            </Helmet>
            <CustomScript scriptText={customJs} />
            <BrowserRouter basename="/public">
                <CookieConsentFooter
                    barionPixelIds={activeBarionPockets
                        .filter((pocket) => pocket.BarionPixelId !== null)
                        .map((pocket) => pocket.BarionPixelId!)} />
                <div className={`App ${isEmbedded ? 'App--embedded' : ''}`}>
                    <div className="App__loading-bar">
                        <BarLoader
                            loading={isLoading}
                            color="var(--primary-select-color)"
                            width="100%"
                            height={6}
                        />
                    </div>
                    <AppProvider value={{ ...providedState, actions: { setUser, setLoading } }}>
                        <ContactProvider value={contactInfo || defaultContactInfo}>
                            <EmbedProvider value={embedOptions}>
                                {!isEmbedded && (
                                    <Header
                                        company={company}
                                        hasContactInfo={hasContactInfo(contactInfo)}
                                    />
                                )}
                                <JsApiContextProvider>
                                    <Main
                                        isEmbedded={isEmbedded}
                                        hideUser={embedOptions.hideUser}
                                        company={company}
                                        isAuthenticated={providedState.isAuthenticated}
                                        contactInfo={contactInfo}
                                        setUser={setUser}
                                        setLoading={setLoading}
                                    />
                                </JsApiContextProvider>
                                {embedOptions.hideFooter || (
                                    <Footer
                                        isEmbedded={isEmbedded}
                                        hasActiveBarionPocket={!!activeBarionPockets?.length}
                                        hasActiveSimplePayPocket={!!activeSimplePayPockets?.length}
                                    />
                                )}
                            </EmbedProvider>
                        </ContactProvider>
                    </AppProvider>
                </div>
            </BrowserRouter>
        </>
    );
};

export default App;
